<template>
  <div
    :class="[`repartition-doughnut__${orientation}`, { 'repartition-doughnut__tooltips': tooltips }]"
    class="repartition-doughnut"
  >
    <div
      :style="[sizeStyles]"
      class="repartition-doughnut-graph"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        :viewBox="`${-size / 2} ${-size / 2} ${size} ${size}`"
      >
        <circle
          cx="0"
          cy="0"
          :r="size /2"
          class="background"
        />

        <RepartitionDoughnutQuarterArc
          v-for="item of data.items"
          :key="item.code"
          :disabled="isItemDisabled(item)"
          :item="item"
          :radius="radius"
          :quartersGap="quartersGap"
          :startValue="getItemStartValue(item)"
          :stroke-width="strokeWidth"
          :tooltip="tooltips"
        />

        <g class="inset">
          <circle
            cx="0"
            cy="0"
            :r="insetRadius"
            class="inset-circle"
          />

          <slot name="inset" />
        </g>
      </svg>

      <div
        :style="[slotSizeStyles]"
        class="slot"
      >
        <slot />
      </div>
    </div>

    <div class="repartition-doughnut-legends">
      <GraphLegend
        v-for="item of data.items"
        :key="item.code"
        :legend="item"
      />
    </div>
  </div>
</template>

<script>
import sum from 'lodash/sum'
import { v4 as uuidv4 } from 'uuid'
import GraphLegend from './graph-legend.vue'
import RepartitionDoughnutQuarterArc from './repartition-doughnut-quarter-arc.vue'

export default {
  name: 'RepartitionDoughnut',

  components: {
    RepartitionDoughnutQuarterArc,
    GraphLegend
  },

  props: {
    value: {
      type: String,
      default: null
    },
    data: {
      type: Object,
      required: true
    },
    orientation: {
      type: String,
      default: 'horizontal'
    },
    tooltips: {
      type: Boolean,
      default: true
    },
    size: {
      type: [Number, String],
      default: 224
    },
    width: {
      type: [Number, String],
      default: 20
    }
  },

  data () {
    return {
      id: uuidv4(),

      padding: 12,
      quartersGap: 0.25,
    }
  },

  computed: {
    sizeStyles () {
      return {
        width: `${this.size}px`,
        height: `${this.size}px`,
        minWidth: `${this.size}px`,
        minHeight: `${this.size}px`
      }
    },

    radius () {
      return this.size / 2 - Number(this.padding)
    },

    strokeWidth () {
      return Number(this.width)
    },

    insetRadius () {
      return this.radius - this.strokeWidth
    },

    slotSizeStyles () {
      return {
        width: `${this.insetRadius * 2}px`,
        height: `${this.insetRadius * 2}px`
      }
    },
  },

  watch: {
    'data.items': {
      handler () {
        this.tooltipsData = this.data.items.map(() => {
          return {
            visible: false
          }
        })
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    isItemDisabled (item) {
      return this.value != null && this.value !== item.code && this.value !== 'TOUS'
    },

    getItemStartValue (item) {
      return sum(
        this.data.items
          .slice(
            0,
            this.data.items.findIndex((i) => i.code === item.code)
          )
          .map((item) => item.percentage)
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.repartition-doughnut {
  display: flex;

  &.repartition-doughnut__horizontal {
    align-items: center;
    gap: 48px;
  }

  &.repartition-doughnut__vertical {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  .repartition-doughnut-legends {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .repartition-doughnut-graph {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 1000000px;
    box-shadow:
      0 3px 1px -2px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
      0 2px 2px 0 var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)),
      0 1px 5px 0 var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.12));

    > svg {
      width: 100%;
      height: 100%;
      overflow: visible;

      .background {
        fill: #ffffff;
        stroke: none;
      }

       .inset {
        filter: drop-shadow(1px 1px 1px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)));

        .inset-circle {
          overflow: visible;
          fill: #ffffff;
          stroke: none;
        }
      }
    }
  }

  &.repartition-doughnut__tooltips {
    :deep(.repartition-doughnut-quarter-arc) {
      &:hover {
        filter: drop-shadow(
          0px 1px 1px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2))
        );
      }
    }
  }

  .slot {
    position: absolute;
    z-index: 2;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border-radius: 1000000px;
  }
}
</style>
